/**
 * Converts an exact major-unit decimal string to a JavaScript-safe minor-unit integer.
 */
export function decimalToMinorUnits(value: string, fractionDigits: number): number {
    const normalized = value.trim();
    if (!/^[+-]?\d+(?:\.\d*)?$/.test(normalized)) {
        throw new Error('Enter a valid decimal amount');
    }
    if (!Number.isInteger(fractionDigits) || fractionDigits < 0) {
        throw new Error('Currency precision is unavailable');
    }

    const negative = normalized.startsWith('-');
    const unsigned = normalized.replace(/^[+-]/, '');
    const [wholePart = '0', enteredFraction = ''] = unsigned.split('.');
    const significantExtraDigits = enteredFraction.slice(fractionDigits);
    if (significantExtraDigits.replace(/0/g, '') !== '') {
        throw new Error(`Amount supports at most ${fractionDigits} decimal places`);
    }

    const fractionPart = enteredFraction.slice(0, fractionDigits).padEnd(fractionDigits, '0');
    const scale = 10n ** BigInt(fractionDigits);
    let minorUnits = BigInt(wholePart) * scale + BigInt(fractionPart || '0');
    if (negative) {
        minorUnits = -minorUnits;
    }

    const maxSafe = BigInt(Number.MAX_SAFE_INTEGER);
    if (minorUnits > maxSafe || minorUnits < -maxSafe) {
        throw new Error('Amount is too large for this currency');
    }

    return Number(minorUnits);
}

/**
 * Converts a JavaScript-safe minor-unit integer to canonical major-unit decimal text.
 */
export function minorUnitsToDecimal(value: number, fractionDigits: number): string {
    if (!Number.isSafeInteger(value)) {
        throw new Error('Minor-unit amount must be a safe integer');
    }
    if (!Number.isInteger(fractionDigits) || fractionDigits < 0) {
        throw new Error('Currency precision is unavailable');
    }

    const negative = value < 0;
    const absolute = BigInt(Math.abs(value));
    const scale = 10n ** BigInt(fractionDigits);
    const whole = absolute / scale;
    const fraction = absolute % scale;
    const fractionText = fraction.toString().padStart(fractionDigits, '0').replace(/0+$/, '');
    const decimal = fractionText ? `${whole}.${fractionText}` : whole.toString();

    return negative && absolute !== 0n ? `-${decimal}` : decimal;
}

/**
 * The smallest representable amount for a currency, as an input `step` value.
 *
 * Runs during render, so a nonsensical precision degrades to the two-digit default
 * rather than throwing and taking the surrounding form down with it.
 */
export function minorUnitStep(fractionDigits: number): string {
    if (!Number.isInteger(fractionDigits) || fractionDigits < 0) {
        return '0.01';
    }

    return fractionDigits === 0 ? '1' : `0.${'0'.repeat(fractionDigits - 1)}1`;
}
